import { useState } from "react";
import dayjs from "dayjs";
import {
  Badge,
  Button,
  Collapse,
  Divider,
  Group,
  List,
  Stack,
  Text,
  Title,
  UnstyledButton,
} from "@mantine/core";
import {
  IconChevronDown,
  IconChevronUp,
  IconInfoCircle,
  IconPlus,
} from "@tabler/icons-react";
import { ClockIcon } from "@heroicons/react/16/solid";

import type {
  NonTransferableReason,
  PensionPolicy,
} from "../../hooks/api/useGetInsurelyFees";
import { companyLabel } from "../../utils/insurelyCompany";
import {
  expiredPolicyCount,
  isPolicyExpired,
  type CollectionOutcomes,
} from "../../utils/insurelyCollections";
import { REPLAY_BLOCK_CLASS } from "../../utils/statsig";
import { OutdatedDataAlert } from "./OutdatedDataAlert";
import { BottomSheet } from "./BottomSheet";
import { DeletePensionData } from "./DeletePensionData";
import classes from "./PensionOverview.module.css";

const REASON_LABEL: Partial<Record<NonTransferableReason, string>> = {
  COLLECTIVE_AGREEMENT: "Kollektivavtalad",
  DEFINED_BENEFIT: "Förmånsbestämd",
  ACTIVE_PAYOUT: "Aktiv utbetalning",
};

const formatSek = (amount: number) =>
  `${Math.round(amount).toLocaleString("sv-SE")} kr`;

const formatDate = (value: string) => dayjs(value).format("YYYY/MM/DD");

const totalValue = (policies: PensionPolicy[]) =>
  policies.reduce((total, policy) => total + policy.currentValue, 0);

function PolicyRow({ policy }: { policy: PensionPolicy }) {
  const reason = policy.nonTransferableReasons[0];

  return (
    <Stack gap="xs" className={classes.row}>
      <Group justify="space-between" align="center" wrap="nowrap">
        <Stack gap={2}>
          <Text fw={700} size="sm">
            {companyLabel(policy.company)}
          </Text>
          {policy.holder?.name && (
            <Text
              c="var(--wallet-color-neutrals-gray-500)"
              size="xs"
              className={REPLAY_BLOCK_CLASS}
            >
              {policy.holder.name}
            </Text>
          )}
          {reason && REASON_LABEL[reason] && (
            <Text size="xs">{REASON_LABEL[reason]}</Text>
          )}
        </Stack>
        {isPolicyExpired(policy) ? (
          <Badge
            size="sm"
            leftSection={<ClockIcon width="10px" aria-hidden="true" />}
          >
            Behöver uppdateras
          </Badge>
        ) : (
          <Text
            fw={700}
            size="sm"
            style={{ whiteSpace: "nowrap" }}
            className={REPLAY_BLOCK_CLASS}
          >
            {formatSek(policy.currentValue)}
          </Text>
        )}
      </Group>
      {policy.savedAt && (
        <>
          <Divider color="var(--wallet-color-neutrals-gray-200)" />
          <Text c="var(--wallet-color-neutrals-gray-500)" size="xs">
            Uppgifter från {formatDate(policy.savedAt)}
          </Text>
        </>
      )}
    </Stack>
  );
}

function PolicyGroup({
  label,
  policies,
  showTotal,
  onInfo,
  infoLabel,
}: {
  label: string;
  policies: PensionPolicy[];
  showTotal: boolean;
  onInfo?: () => void;
  infoLabel?: string;
}) {
  if (policies.length === 0) return null;

  const labelText = (
    <Text span size="sm" c="var(--wallet-color-neutrals-gray-500)" fw={700}>
      {label}
    </Text>
  );

  return (
    <Stack gap="xs">
      <Group justify="space-between" gap="xs">
        {onInfo ? (
          // The label is part of the hit area, so the icon is not the only way in.
          <UnstyledButton
            onClick={onInfo}
            aria-label={infoLabel}
            className={classes.infoButton}
          >
            {labelText}
            <IconInfoCircle size={14} aria-hidden="true" />
          </UnstyledButton>
        ) : (
          labelText
        )}
        {showTotal && (
          <Text
            size="sm"
            c="var(--wallet-color-neutrals-gray-500)"
            fw={700}
            className={REPLAY_BLOCK_CLASS}
          >
            {formatSek(totalValue(policies))}
          </Text>
        )}
      </Group>
      {policies.map((policy) => (
        <PolicyRow key={policy.policyId} policy={policy} />
      ))}
    </Stack>
  );
}

function CompanyList({
  label,
  companies,
}: {
  label: string;
  companies: string[];
}) {
  if (companies.length === 0) return null;

  return (
    <Stack gap="xs">
      <Text size="sm" c="var(--wallet-color-neutrals-gray-500)" fw={700}>
        {label}
      </Text>
      {companies.map((company) => (
        <Text key={company} fw={700} size="sm" className={classes.row}>
          {companyLabel(company)}
        </Text>
      ))}
    </Stack>
  );
}

function UnresolvedCollections({ outcomes }: { outcomes: CollectionOutcomes }) {
  const [open, setOpen] = useState(false);

  if (outcomes.empty.length === 0 && outcomes.failed.length === 0) return null;

  return (
    <Stack gap="xs">
      <UnstyledButton
        onClick={() => setOpen((wasOpen) => !wasOpen)}
        aria-expanded={open}
      >
        <Group justify="space-between">
          <Text fw={700}>Sökningar utan resultat</Text>
          {open ? (
            <IconChevronUp size={18} aria-hidden="true" />
          ) : (
            <IconChevronDown size={18} aria-hidden="true" />
          )}
        </Group>
      </UnstyledButton>
      <Collapse in={open}>
        <Divider color="var(--wallet-color-neutrals-gray-100)" mb="md" />
        <Stack gap="md">
          <CompanyList label="Kunde inte hämtas" companies={outcomes.failed} />
          <CompanyList
            label="Ingen pension hittades"
            companies={outcomes.empty}
          />
        </Stack>
      </Collapse>
    </Stack>
  );
}

interface PensionOverviewProps {
  policies: PensionPolicy[];
  onAddMore: () => void;
  onRefresh: () => void;
  onDelete?: () => Promise<boolean>;
  outcomes?: CollectionOutcomes;
  deleted?: boolean;
}

export function PensionOverview({
  policies,
  onAddMore,
  onRefresh,
  onDelete,
  outcomes,
  deleted,
}: PensionOverviewProps) {
  const [infoOpen, setInfoOpen] = useState(false);

  // Split by "can we lower the fee", not by transferability.
  const improvable = policies.filter(
    (policy) => policy.nonTransferableReasons.length === 0,
  );
  const locked = policies.filter(
    (policy) => policy.nonTransferableReasons.length > 0,
  );

  // Nothing to group or total: the invitation to search replaces the whole overview.
  const isEmpty = policies.length === 0;

  // No split to explain, so the group is named after what it holds.
  const onlyLocked = improvable.length === 0;
  const expiredCount = expiredPolicyCount(policies);

  const emptyText = deleted
    ? "Dina hämtade pensionsuppgifter är borttagna. Du kan alltid hämta dem igen om du vill fylla på översikten."
    : "Vi hittade inga pensioner hos de bolag du valde. Leta efter fler pensioner för att fylla på din översikt.";

  const infoTitle = onlyLocked
    ? "Vad visas i översikten?"
    : "Vilka pensioner kan jag inte påverka?";

  return (
    <Stack gap="lg">
      <Stack gap="xs" align="center">
        <Title
          order={1}
          fz="h2"
          lh="h2"
          ff="var(--wallet-font-family-sambla-sans)"
          ta="center"
        >
          Pensionsöversikt
        </Title>
        <Text c="dimmed" size="md" ta="center" lh={1.5}>
          {isEmpty
            ? emptyText
            : "Här ser du de tjänste- och privatpensioner vi har hittat, så att du enklare kan få koll på ditt pensionssparande."}
        </Text>
        {isEmpty && (
          <Button
            fullWidth
            mt="xs"
            variant="default"
            rightSection={<IconPlus size={16} aria-hidden="true" />}
            onClick={onAddMore}
          >
            {deleted ? "Hämta nya uppgifter" : "Leta efter fler pensioner"}
          </Button>
        )}
      </Stack>

      {expiredCount > 0 && (
        <OutdatedDataAlert
          expiredCount={expiredCount}
          totalCount={policies.length}
          onRefresh={onRefresh}
        />
      )}

      {!isEmpty && (
        <>
          <Stack gap="xs">
            <Group justify="space-between">
              <Title order={2} fz="md" lh="md" fw={700}>
                Dina pensioner
              </Title>
              <Button
                variant="subtle"
                size="compact-sm"
                c="var(--wallet-color-neutrals-black)"
                leftSection={<IconPlus size={16} aria-hidden="true" />}
                onClick={onAddMore}
              >
                <Text fw={400} size="sm">
                  Leta efter fler
                </Text>
              </Button>
            </Group>
            <Divider color="var(--wallet-color-neutrals-gray-100)" />
          </Stack>

          <PolicyGroup
            label="Möjlig att påverka"
            policies={improvable}
            showTotal={expiredCount === 0}
          />
          <PolicyGroup
            label={
              onlyLocked ? "Pensioner vi hittat" : "Inte möjlig att påverka"
            }
            policies={locked}
            showTotal={expiredCount === 0}
            onInfo={() => setInfoOpen(true)}
            infoLabel={infoTitle}
          />
        </>
      )}

      {outcomes && <UnresolvedCollections outcomes={outcomes} />}

      {!isEmpty && onDelete && <DeletePensionData onDelete={onDelete} />}

      <BottomSheet
        opened={infoOpen}
        onClose={() => setInfoOpen(false)}
        title={infoTitle}
      >
        <Stack gap="md">
          {onlyLocked ? (
            <Text size="sm" lh={1.6}>
              Översikten visar de tjänste- och privatpensioner som är
              tillgängliga för oss. Din statliga pension visas inte här och
              pensioner från kommunal och regional anställning kan även saknas,
              till exempel Kåpan Tjänstepension och KPA Pension.
            </Text>
          ) : (
            <>
              <Text size="sm" lh={1.6}>
                Vissa pensioner går inte att påverka, vilket innebär att
                avgifterna oftast inte kan sänkas. Det kan bero på att de är
                kollektivavtalade eller har en aktiv inbetalning.
              </Text>
              <Text size="sm" fw={700}>
                Vanliga exempel:
              </Text>
              <List spacing="xs" size="sm">
                <List.Item>
                  Kollektivavtalade tjänstepensioner (avtalspensioner), som
                  oftast redan har bra villkor.
                </List.Item>
                <List.Item>
                  Pensioner som fortfarande har aktiva inbetalningar, exempelvis
                  hos din nuvarande arbetsgivare.
                </List.Item>
                <List.Item>
                  Individuella pensioner med traditionell livförvaltning.
                </List.Item>
              </List>
            </>
          )}
          <Button
            fullWidth
            variant="default"
            onClick={() => setInfoOpen(false)}
          >
            Stäng
          </Button>
        </Stack>
      </BottomSheet>
    </Stack>
  );
}
